import {
  Badge,
  Button,
  Group,
  Image,
  Table,
  Text,
  Tooltip
} from '@mantine/core'
import { uniqBy } from 'lodash-es'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { ApplicationNestedPath, buildAppLink } from '@/router/paths'
import {
  CrossCameraIdentity,
  CrossCameraMatchQuality
} from '@/types/crossCamera'
import { getFormattedDateWithTime } from '@/utils/date'
import { objectToQueryString } from '@/utils/url'
import { TRACKING_ID_PARAM_KEY } from '../../events/helpers/searchParams'
import { CrossCameraCameraList } from '../CrossCameraCameraList/CrossCameraCameraList'

const CROP_HEIGHT = 72

const MATCH_BADGES: Record<
  CrossCameraMatchQuality,
  { color: string; translationId: string }
> = {
  [CrossCameraMatchQuality.Strong]: {
    color: 'green',
    translationId: 'crossCamera.people.match.strong'
  },
  [CrossCameraMatchQuality.Good]: {
    color: 'yellow',
    translationId: 'crossCamera.people.match.good'
  },
  [CrossCameraMatchQuality.Weak]: {
    color: 'red',
    translationId: 'crossCamera.people.match.weak'
  },
  [CrossCameraMatchQuality.Face]: {
    color: 'yellow',
    translationId: 'crossCamera.people.match.face'
  },
  [CrossCameraMatchQuality.ByHand]: {
    color: 'gray',
    translationId: 'crossCamera.people.match.byHand'
  }
}

const camerasOf = (identity: CrossCameraIdentity) =>
  uniqBy(identity.tracklets, 'camera_id').map(({ camera_id, camera_name }) => ({
    camera_id,
    camera_name
  }))

type CrossCameraIdentityListProps = {
  identities: CrossCameraIdentity[]
  isLoading?: boolean
  hasError?: boolean
  errorMessage?: ReactNode
  onReload?: () => void
  onDeleteClick?: (identityId: string, appId: string) => void
}

export const CrossCameraIdentityList = ({
  identities,
  isLoading,
  hasError,
  errorMessage,
  onReload,
  onDeleteClick
}: CrossCameraIdentityListProps) => {
  const intl = useIntl()

  return (
    <DataTable
      hasItems={identities.length > 0}
      isLoading={isLoading}
      hasError={hasError}
      emptyMessage={<FormattedMessage id="crossCamera.people.empty" />}
      errorMessage={
        errorMessage ?? <FormattedMessage id="crossCamera.people.error" />
      }
      onReload={onReload}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="crossCamera.people.fields.person" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="crossCamera.people.fields.cameras" />
          </Table.Th>
          <Table.Th>
            <Tooltip
              label={<FormattedMessage id="crossCamera.people.match.help" />}
              w={280}
              multiline
            >
              <span>
                <FormattedMessage id="crossCamera.people.fields.match" />
              </span>
            </Tooltip>
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="crossCamera.people.fields.firstSeen" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="crossCamera.people.fields.lastSeen" />
          </Table.Th>
          <Table.Th ta="right">
            <FormattedMessage id="crossCamera.people.fields.actions" />
          </Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {identities.map((identity) => (
          <Table.Tr key={identity.global_id}>
            <Table.Td>
              {identity.crop_path ? (
                <Image
                  h={CROP_HEIGHT}
                  w="auto"
                  fit="contain"
                  src={identity.crop_path}
                  alt={intl.formatMessage({ id: 'crossCamera.people.crop' })}
                />
              ) : (
                <Text size="sm" c="dimmed">
                  <FormattedMessage id="crossCamera.people.noPicture" />
                </Text>
              )}
            </Table.Td>

            <Table.Td>
              <CrossCameraCameraList cameras={camerasOf(identity)} />
            </Table.Td>

            <Table.Td>
              {identity.match_quality ? (
                <Tooltip
                  label={
                    <FormattedMessage
                      id={MATCH_BADGES[identity.match_quality].translationId}
                    />
                  }
                >
                  <Badge
                    size="md"
                    variant="light"
                    ff="monospace"
                    color={MATCH_BADGES[identity.match_quality].color}
                  >
                    {identity.match_score != null ? (
                      identity.match_score.toFixed(2)
                    ) : (
                      <FormattedMessage id="crossCamera.people.match.byHandShort" />
                    )}
                  </Badge>
                </Tooltip>
              ) : (
                <Text size="sm" c="dimmed">
                  <FormattedMessage id="crossCamera.people.match.unknown" />
                </Text>
              )}
            </Table.Td>

            <Table.Td>
              <Text size="sm">
                {getFormattedDateWithTime(identity.first_seen)}
              </Text>
            </Table.Td>

            <Table.Td>
              <Text size="sm">
                {getFormattedDateWithTime(identity.last_seen)}
              </Text>
            </Table.Td>

            <Table.Td>
              <Group gap="xs" justify="end" wrap="nowrap">
                <Button
                  component={Link}
                  size="compact-sm"
                  variant="subtle"
                  to={`${buildAppLink(identity.app_id, ApplicationNestedPath.events)}?${objectToQueryString({ [TRACKING_ID_PARAM_KEY]: identity.global_id })}`}
                >
                  <FormattedMessage id="events.view" />
                </Button>
                {onDeleteClick && (
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="red"
                    onClick={() =>
                      onDeleteClick(identity.global_id, identity.app_id)
                    }
                  >
                    <FormattedMessage id="delete" />
                  </Button>
                )}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </DataTable>
  )
}
